import { Avatar, Box, Button, Chip, Stack, Typography } from "@mui/material";
import React from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/AuthContext";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import { FormInputText } from "../../components/common/FormInputText";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import useApiMutation from "../../hooks/useApiMutation";
import {
  editCustomerDetails,
  getCustomerDetails,
} from "../../utilities/service";
import { updateProfileSchema } from "../../utilities/schema";
import useApiQuery from "../../hooks/useApiQuery";
import { ProfileSkeleton } from "./ProfileSkeleton";

function Profile() {
  const { user } = useAuth();

  const {
    data: customerDetails,
    error,
    isLoading,
    refetch,
  } = useApiQuery({
    queryKey: ["getCustomerDetails"],
    queryFn: () => getCustomerDetails(user),
  });
  const { mutate, isPending } = useApiMutation({
    mutationFn: (param) => editCustomerDetails(param),
    options: {
      onSuccess: (data, variables) => {
        if (!data?.error) {
          refetch();
          reset();
          toast.success(data?.message);
        } else {
          toast.error(data?.message);
        }
      },
      onError: (error) => {
        toast.error(error?.message);
      },
    },
  });
  const methods = useForm({
    resolver: yupResolver(updateProfileSchema),
  });
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = methods;

  const updateProfile = async (data) => {
    const param = {
      customer_id: user?.customer_id,
      ...data,
    };
    mutate(param);
  };
  const profileInitial = customerDetails?.customer_name?.charAt(0)?.toUpperCase() || "U";

  if (isLoading) return <ProfileSkeleton />;
  return (
    <Box>
      <ProfileHeroCard>
        <ProfileHeroContent>
          <Avatar
            sx={{
              height: 110,
              width: 110,
              fontSize: "48px",
              border: "3px solid rgba(188, 128, 15, 0.82)",
              boxShadow: "0 0 0 8px rgba(255, 247, 232, 0.92)",
              bgcolor: "#F3E4C4",
              color: "#7B5311",
            }}
          >
            {profileInitial}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: "24px", md: "30px" },
                lineHeight: "130%",
                color: "#2B241A",
                mb: 0.5,
              }}
            >
              {customerDetails?.customer_name || "Your profile"}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#6E624B",
                mb: 2,
                maxWidth: 520,
              }}
            >
              Keep your account details up to date for smooth pan-India order updates, delivery coordination, and checkout support.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <ProfileBadge
                icon={<VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Verified account"
              />
              <ProfileBadge
                icon={<LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Pan-India ready"
              />
            </Stack>
          </Box>
        </ProfileHeroContent>
      </ProfileHeroCard>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          fontSize: "20px",
          lineHeight: "150%",
          color: "var(--neutral-black)",
          textAlign: "left",
          position: "relative",
          pb: 1,
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "44px",
            height: "3px",
            borderRadius: "2px",
            background: "var(--primary-red-base)",
          },
        }}
      >
        Account Details
      </Typography>

      <Grid container spacing={3} sx={{ mt: 4 }}>
        <Grid
          size={{ xs: 12, md: 4 }}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <InfoCard>
            <InfoTitle>Account Snapshot</InfoTitle>
            <InfoRow>
              <PhoneIphoneRoundedIcon sx={{ fontSize: 18, color: "#A86E18" }} />
              <Box>
                <InfoLabel>Mobile</InfoLabel>
                <InfoValue>{customerDetails?.customer_mobile || "Not added"}</InfoValue>
              </Box>
            </InfoRow>
            <InfoRow>
              <MailOutlineRoundedIcon sx={{ fontSize: 18, color: "#A86E18" }} />
              <Box>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{customerDetails?.customer_email || "Not added"}</InfoValue>
              </Box>
            </InfoRow>
          </InfoCard>

          <InfoCard>
            <InfoTitle>Account Notes</InfoTitle>
            <Typography sx={{ fontSize: "13px", color: "#6E624B", lineHeight: 1.6 }}>
              Your mobile number stays locked to protect order history and login continuity. You can update your name and email here anytime.
            </Typography>
          </InfoCard>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <FormCard>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormInputText
                  name="customer_name"
                  control={control}
                  label="Full Name"
                  errors={errors}
                  type="text"
                  maxLength={25}
                  autofocus={true}
                  defaultValue={customerDetails?.customer_name}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormInputText
                  name="customer_email"
                  control={control}
                  label="Email Address"
                  errors={errors}
                  type="text"
                  maxLength={50}
                  defaultValue={customerDetails?.customer_email}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormInputText
                  name="customer_mobile"
                  control={control}
                  label="Mobile Number"
                  errors={errors}
                  type="text"
                  disabled={true}
                  defaultValue={customerDetails?.customer_mobile}
                />
              </Grid>
              <Grid size={12}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#7C6B4D",
                    textAlign: "left",
                  }}
                >
                  Use the same contact details you want to receive order, payment, and dispatch updates on.
                </Typography>
              </Grid>
              <Grid
                size={12}
                sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}
              >
                <Button
                  onClick={handleSubmit(updateProfile)}
                  variant="contained"
                  sx={{
                    py: 1.5,
                    fontSize: "16px",
                    height: "48px",
                    px: 6,
                    borderRadius: "999px",
                  }}
                  disabled={isPending}
                >
                  {isPending ? "Updating..." : "Save Profile"}
                </Button>
              </Grid>
            </Grid>
          </FormCard>
        </Grid>
      </Grid>
    </Box>
  );
}

const ProfileHeroCard = styled(Box)(({ theme }) => ({
  border: "1px solid rgba(183, 124, 10, 0.24)",
  borderRadius: "18px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  background:
    "radial-gradient(circle at top left, rgba(255,247,231,0.95) 0%, rgba(255,255,255,0.98) 48%, rgba(255,250,242,0.95) 100%)",
  boxShadow: "0 14px 30px rgba(173, 132, 61, 0.1)",
}));

const ProfileHeroContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

const ProfileBadge = styled(Chip)(() => ({
  borderRadius: "999px",
  background: "rgba(255, 246, 225, 0.95)",
  border: "1px solid rgba(188, 128, 15, 0.24)",
  color: "#6B5425",
  fontWeight: 600,
}));

const InfoCard = styled(Box)(({ theme }) => ({
  border: "1px solid rgba(183, 124, 10, 0.18)",
  borderRadius: "16px",
  padding: theme.spacing(2),
  background: "rgba(255,255,255,0.95)",
  boxShadow: "0 8px 20px rgba(173, 132, 61, 0.06)",
}));

const FormCard = styled(Box)(({ theme }) => ({
  border: "1px solid rgba(183, 124, 10, 0.18)",
  borderRadius: "16px",
  padding: theme.spacing(2.5),
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(255,250,242,0.94) 100%)",
  boxShadow: "0 10px 24px rgba(173, 132, 61, 0.08)",
}));

const InfoTitle = styled(Typography)(() => ({
  fontSize: "15px",
  fontWeight: 700,
  color: "#2B241A",
  marginBottom: "14px",
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.25),
  marginBottom: theme.spacing(1.5),
  '&:last-of-type': {
    marginBottom: 0,
  },
}));

const InfoLabel = styled(Typography)(() => ({
  fontSize: "12px",
  color: "#7C6B4D",
  marginBottom: "2px",
}));

const InfoValue = styled(Typography)(() => ({
  fontSize: "14px",
  fontWeight: 600,
  color: "#30281B",
  lineHeight: 1.5,
}));

export default Profile;
